import { useState } from 'react';
import { DEFAULT_JOBS_COLOR_PALETTE, ENGAGEMENT_TYPE_COLOR_MAP, JOBS_COLOR_PALETTE_KEY, JOBS_COLOR_PALETTE_PRESETS_KEY, JOBS_EVENT_COLORS, JOBS_STATUS_LABELS, JobsColorPalette } from 'dashboard409a/pages/Workboard/common/constants';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { Logger } from 'utilities/logger';

export type ColorPalettePreset = {
  name: string;
  palette: JobsColorPalette;
};

// Utility functions for color palette management
export const getJobsColorPalette = (): JobsColorPalette => {
  try {
    const saved = window.localStorage.getItem(JOBS_COLOR_PALETTE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<JobsColorPalette>;
      return {
        ...DEFAULT_JOBS_COLOR_PALETTE,
        ...parsed,
      };
    }
  } catch {
    return DEFAULT_JOBS_COLOR_PALETTE;
  }

  return DEFAULT_JOBS_COLOR_PALETTE;
};

export const getEngagementTypeColor = (engagementType: string): string => ENGAGEMENT_TYPE_COLOR_MAP[engagementType as keyof typeof ENGAGEMENT_TYPE_COLOR_MAP] || '#000000';

export const saveJobsColorPalette = (palette: Partial<JobsColorPalette>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const current = getJobsColorPalette();
    const merged = { ...current, ...palette };
    window.localStorage.setItem(JOBS_COLOR_PALETTE_KEY, JSON.stringify(merged));
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: { key: JOBS_COLOR_PALETTE_KEY, value: merged }
    }));
  } catch {
    Logger.error('Error saving jobs color palette', new Error('Error saving jobs color palette'));
  }
};

export const resetJobsColorPalette = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(JOBS_COLOR_PALETTE_KEY);
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: { key: JOBS_COLOR_PALETTE_KEY, value: DEFAULT_JOBS_COLOR_PALETTE }
    }));
  } catch {
    Logger.error('Error resetting jobs color palette', new Error('Error resetting jobs color palette'));
  }
};

// Get status color with localStorage fallback
export const getStatusColor = (status: number): string => {
  const palette = getJobsColorPalette();
  const paletteKeys = Object.keys(JOBS_STATUS_LABELS) as Array<keyof JobsColorPalette>;
  const paletteKey = paletteKeys[status];
  
  return paletteKey ? palette[paletteKey] : JOBS_EVENT_COLORS.DEFAULT;
};

// Helper to convert color with alpha to solid color for color input
export const toSolidColor = (color: string): string => {
  if (!color) {
    return '#000000';
  }
  // If color has 8 characters (with alpha), strip the alpha
  if (color.length === 9 && color.startsWith('#')) {
    return color.slice(0, 7);
  }
  // If color is rgba format, convert to hex
  if (color.startsWith('rgba')) {
    const matches = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (matches) {
      const r = parseInt(matches[1], 10).toString(16).padStart(2, '0');
      const g = parseInt(matches[2], 10).toString(16).padStart(2, '0');
      const b = parseInt(matches[3], 10).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }
  }
  return color;
};

// Preset management functions
export const getColorPalettePresets = (): ColorPalettePreset[] => {
  try {
    const saved = window.localStorage.getItem(JOBS_COLOR_PALETTE_PRESETS_KEY);
    if (saved) {
      return JSON.parse(saved) as ColorPalettePreset[];
    }
  } catch {
    return [];
  }

  return [];
};

export const saveColorPalettePreset = (name: string, palette: JobsColorPalette): void => {
  try {
    const presets = getColorPalettePresets();
    const existingIndex = presets.findIndex(p => p.name === name);
    
    if (existingIndex >= 0) {
      presets[existingIndex] = { name, palette };
    } else {
      presets.push({ name, palette });
    }

    window.localStorage.setItem(JOBS_COLOR_PALETTE_PRESETS_KEY, JSON.stringify(presets));
  } catch {
    Logger.error('Error saving color palette preset', new Error('Error saving color palette preset'));
  }
};

export const deleteColorPalettePreset = (name: string): void => {

  try {
    const presets = getColorPalettePresets();
    const filtered = presets.filter(p => p.name !== name);
    window.localStorage.setItem(JOBS_COLOR_PALETTE_PRESETS_KEY, JSON.stringify(filtered));
  } catch {
    Logger.error('Error deleting color palette preset', new Error('Error deleting color palette preset'));
  }
};

export const applyColorPalettePreset = (name: string): boolean => {
  try {
    const presets = getColorPalettePresets();
    const preset = presets.find(p => p.name === name);
    
    if (preset) {
      saveJobsColorPalette(preset.palette);
      return true;
    }
  } catch {
    Logger.error('Error applying color palette preset', new Error('Error applying color palette preset'));
  }

  return false;
};

/**
 * Custom hook for managing color palette state and operations
 * @returns Object containing colorPalette, presets, and handler functions
 */
export const useJobsColorPalette = () => {
  const [colorPalette, setColorPalette] = useLocalStorage<JobsColorPalette>(
    JOBS_COLOR_PALETTE_KEY,
    DEFAULT_JOBS_COLOR_PALETTE
  );
  const [presets, setPresets] = useLocalStorage<ColorPalettePreset[]>(
    JOBS_COLOR_PALETTE_PRESETS_KEY,
    []
  );
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  const handleColorChange = (key: keyof JobsColorPalette, color: string) => {
    setColorPalette(prev => ({ ...prev, [key]: color }));
  };

  const handleReset = () => {
    setColorPalette(DEFAULT_JOBS_COLOR_PALETTE);
  };

  const handleSavePreset = () => {
    if (presetName.trim()) {
      const existingIndex = presets.findIndex(p => p.name === presetName.trim());
      const updatedPresets = existingIndex >= 0
        ? presets.map((p, idx) => idx === existingIndex ? { name: presetName.trim(), palette: colorPalette } : p)
        : [...presets, { name: presetName.trim(), palette: colorPalette }];
      setPresets(updatedPresets);
      setPresetName('');
      setSavePresetOpen(false);
    }
  };

  const handleOpenSavePreset = () => {
    setSavePresetOpen(true);
  };

  const handleCloseSavePreset = () => {
    setSavePresetOpen(false);
    setPresetName('');
  };

  const handleApplyPreset = (name: string) => {
    const preset = presets.find(p => p.name === name);
    
    if (preset) {
      setColorPalette(preset.palette);
    }
  };

  const handleDeletePreset = (name: string) => {
    setPresets(presets.filter(p => p.name !== name));
  };

  return {
    colorPalette,
    presets,
    savePresetOpen,
    presetName,
    setPresetName,
    handleColorChange,
    handleReset,
    handleSavePreset,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleApplyPreset,
    handleDeletePreset,
  };
};